import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

interface RouterContextValue {
  path: string;
  navigate: (to: string, options?: { replace?: boolean }) => void;
}

const RouterContext = createContext<RouterContextValue | undefined>(undefined);

function currentPath(): string {
  if (typeof window === "undefined") {
    return "/";
  }
  return window.location.pathname || "/";
}

export function Router({ children }: { children: React.ReactNode }) {
  const [path, setPath] = useState<string>(() => currentPath());

  useEffect(() => {
    const onPopState = () => setPath(currentPath());
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const navigate = useCallback((to: string, options?: { replace?: boolean }) => {
    if (typeof window === "undefined") {
      return;
    }
    if (to === window.location.pathname) {
      return;
    }
    if (options?.replace === true) {
      window.history.replaceState({}, "", to);
    } else {
      window.history.pushState({}, "", to);
    }
    setPath(to);
  }, []);

  const value = useMemo<RouterContextValue>(() => ({ path, navigate }), [path, navigate]);

  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>;
}

export function useRouter(): RouterContextValue {
  const value = useContext(RouterContext);
  if (value === undefined) {
    throw new Error("useRouter must be used within Router");
  }
  return value;
}

interface LinkProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  to: string;
}

export function Link({ to, onClick, children, ...rest }: LinkProps) {
  const { navigate } = useRouter();
  return (
    <a
      href={to}
      onClick={(event) => {
        if (onClick !== undefined) {
          onClick(event);
        }
        if (event.defaultPrevented) {
          return;
        }
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) {
          return;
        }
        event.preventDefault();
        navigate(to);
      }}
      {...rest}
    >
      {children}
    </a>
  );
}

// Route matching helpers.
//
// We don't ship a query DSL — admin/people/etc are static paths. For the
// gumballs/admin split it is enough to ask "does the current path start
// with /admin?" or "does it match exactly /admin/seasons?".

export function pathMatches(path: string, pattern: string): boolean {
  return path === pattern || path === `${pattern}/`;
}

export function pathStartsWith(path: string, pattern: string): boolean {
  if (pathMatches(path, pattern)) {
    return true;
  }
  return path.startsWith(pattern + "/");
}
